import React, {useMemo} from 'react';
import EditableTable from '../editableTable/editableTable';
import CustomModal from '../../../../../../../../components/custom-modal/custom-modal';
import TextArea from '../../../../../../../../components/form/textArea/TextArea';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import { IconButton } from '@mui/material';
import { FUNCTIONAL_SAFETY_CHANNEL_TYPES } from '../../../../../../../../utils/constants';
import useDCavg from '../../../../../../../../hooks/useDCavg';
import { Box } from '@mui/system';
import ViewFeedback from '../../../../../../../feedback/view-feedback';
import ViewInfo from '../../../../../components/view-info/view-info';
import dcInputImage from '../../../../../../../../assets/images/dc-input.png';
import dcOutputImage from '../../../../../../../../assets/images/dc-output.png';
import dcOutput2Image from '../../../../../../../../assets/images/dc-output-2.png';
import dcLogicImage from '../../../../../../../../assets/images/dc-logic.png';
// Channel Enum
const CHANNEL_TYPES = FUNCTIONAL_SAFETY_CHANNEL_TYPES;

const DCavg = ({
  setShow,
  show,
  assessment,
  groupedMTTFData,
  dcavgAssessments,
  hsrAssessments,
  createDCavg,
  updateDCavg,
  deleteDCavg,
  isCreating,
  isUpdating,
  isDeleting,
}) => {
  // Use the DCavg hook for all logic
  const {
    newRows,
    editingRow,
    editedData,
    shouldShowTeOte,
    shouldShowChannel2,
    hasUnsavedRows,
    dcAvgChannel1,
    dcAvgHalfChannel,
    dcAvgChannel2,
    finalDCavg,
    hsrEquivalentDCavg,
    hsrScore,
    findDCavgForMTTF,
    findNewRowForMTTF,
    addDCForMTTF,
    handleNewRowChange,
    handleRemoveNewRow,
    handleSaveAllNewRows,
    handleStartEdit,
    handleEditChange,
    handleSaveEdit,
    handleCancelEdit,
    handleDelete,
    isCreating: isCreatingFromHook,
    isUpdating: isUpdatingFromHook,
    isDeleting: isDeletingFromHook,
  } = useDCavg({
    assessment,
    groupedMTTFData,
    dcavgAssessments,
    hsrAssessments,
    createDCavg,
    updateDCavg,
    deleteDCavg,
    isCreating,
    isUpdating,
    isDeleting,
  });

  const tableHeaders = ['Item', 'Part Number', 'Part Description', 'DC(%)',"Note", 'Action'];

  const tableKeys = [
    'item',
    'part_number',
    'part_description',
    'dc',
    'note',
    'action',
  ];

  const generateTableData = type => {
    const mttfItems = groupedMTTFData?.[type] || [];

    return mttfItems.map((mttfItem, index) => {
      const existingDC = findDCavgForMTTF(mttfItem.id);
      const newDC = findNewRowForMTTF(type, mttfItem.id);

      const isEditing = editingRow?.type === type && editingRow?.mttfId === mttfItem.id;
      const isNew = !!newDC;

      return tableKeys.map(key => {
        if (key === 'item') {
          return index + 1;
        }

        if (key === 'part_number') {
          return mttfItem.part_number || '-';
        }

        if (key === 'part_description') {
          return mttfItem.part_description || '-';
        }

        if (key === 'dc') {
          if (isEditing) {
            return (
              <input
                type="number"
                value={editedData.dc !== undefined && editedData.dc !== null ? editedData.dc : ''}
                onChange={e => {
                  const value = e.target.value;
                  // Allow empty string for typing
                  if (value === '') {
                    handleEditChange('dc', '');
                    return;
                  }
                  // DC range: 0-99% (per document requirement)
                  const numValue = parseFloat(value);
                  if (!isNaN(numValue) && numValue >= 0 && numValue <= 99) {
                  handleEditChange('dc', value);
                  }
                }}
                style={{width: '100%'}}
                min="0"
                max="99"
                step="0.01"
              />
            );
          }

          if (isNew) {
            return (
              <input
                type="number"
                value={newDC.dc !== undefined && newDC.dc !== null ? newDC.dc : ''}
                onChange={e => {
                  const value = e.target.value;
                  // Allow empty string for typing
                  if (value === '') {
                    handleNewRowChange(type, newDC.tempId, 'dc', '');
                    return;
                  }
                  // DC range: 0-99% (per document requirement)
                  const numValue = parseFloat(value);
                  if (!isNaN(numValue) && numValue >= 0 && numValue <= 99) {
                  handleNewRowChange(type, newDC.tempId, 'dc', value);
                  }
                }}
                style={{width: '100%'}}
                min="0"
                max="99"
                step="0.01"
              />
            );
          }

          if (existingDC) {
            return `${(existingDC.dc * 100).toFixed(2)}`;
          }

          return '-';
        }

        if (key === 'note') {
          if (isEditing) {
            return (
              <TextArea
                label={''}
                height={"80px"}
                value={editedData.note !== undefined && editedData.note !== null ? editedData.note : ''}
                onChange={e => {
                  const value = e.target.value;
                  handleEditChange('note', value);
                }}
                placeholder="Enter notes..."
              />
            );
          }

          if (isNew) {
            return (
              <TextArea
                label={''}
                height={"80px"}
                value={newDC.note || ''}
                onChange={e => handleNewRowChange(type, newDC.tempId, 'note', e.target.value)}
                placeholder="Enter notes..."
              />
            );
          }

          if (existingDC) {
            return existingDC.note ? (
              <div style={{
                whiteSpace: 'pre-wrap',
                wordWrap: 'break-word',
                maxWidth: '100%',
                padding: '4px 0'
              }}>
                {existingDC.note}
              </div>
            ) : '-';
          }

          return '-';
        }

        if (key === 'action') {
          if (isEditing) {
            return (
              <div style={{display: 'flex', gap: '4px'}}>
                <IconButton
                  size="small"
                  onClick={handleSaveEdit}
                  disabled={isUpdatingFromHook}
                  color="primary"
                >
                  <SaveIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={handleCancelEdit}
                  disabled={isUpdatingFromHook}
                >
                  <CancelIcon fontSize="small" />
                </IconButton>
              </div>
            );
          }

          if (isNew) {
            return (
              <div style={{display: 'flex', gap: '4px'}}>
                <IconButton
                  size="small"
                  onClick={() => handleRemoveNewRow(type, newDC.tempId)}
                  disabled={isCreatingFromHook}
                  color="error"
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </div>
            );
          }

          if (existingDC) {
            return (
              <div style={{display: 'flex', gap: '4px'}}>
                <IconButton
                  size="small"
                  onClick={() => handleStartEdit(type, mttfItem, existingDC)}
                  disabled={isDeletingFromHook}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => handleDelete(existingDC.id)}
                  disabled={isDeletingFromHook}
                  color="error"
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </div>
            );
          }

          return (
            <IconButton
              size="small"
              onClick={() => addDCForMTTF(type, mttfItem)}
              color="primary"
              disabled={isCreatingFromHook}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          );
        }

        return '-';
      });
    });
  };

  // Channel 1 tables (Input, Logic, Output)
  const channel1Tables = useMemo(() => {
    return [
      {heading: 'Input (I1)', type: CHANNEL_TYPES.INPUT, data: generateTableData(CHANNEL_TYPES.INPUT)},
      {heading: 'Logic (L1)', type: CHANNEL_TYPES.LOGIC, data: generateTableData(CHANNEL_TYPES.LOGIC)},
      {heading: 'Output (O1)', type: CHANNEL_TYPES.OUTPUT, data: generateTableData(CHANNEL_TYPES.OUTPUT)},
    ];
  }, [groupedMTTFData, editingRow, newRows, editedData, findDCavgForMTTF]);

  // Half Channel tables (TE, OTE)
  const halfChannelTables = useMemo(() => {
    if (!shouldShowTeOte) {
      return [];
    }
    return [
        {heading: 'Test Equipment (TE)', type: CHANNEL_TYPES.TE, data: generateTableData(CHANNEL_TYPES.TE)},
        {heading: 'Other Test Equipment (OTE)', type: CHANNEL_TYPES.OTE, data: generateTableData(CHANNEL_TYPES.OTE)}
    ];
  }, [groupedMTTFData, shouldShowTeOte, editingRow, newRows, editedData, findDCavgForMTTF]);

  // Channel 2 tables (separate from Channel 1)
  const channel2Tables = useMemo(() => {
    if (!shouldShowChannel2) {
      return [];
    }

    return [
      {heading: 'Input (I2)', type: CHANNEL_TYPES.INPUT2, data: generateTableData(CHANNEL_TYPES.INPUT2)},
      {heading: 'Logic (L2)', type: CHANNEL_TYPES.LOGIC2, data: generateTableData(CHANNEL_TYPES.LOGIC2)},
      {heading: 'Output (O2)', type: CHANNEL_TYPES.OUTPUT2, data: generateTableData(CHANNEL_TYPES.OUTPUT2)}
    ];
  }, [groupedMTTFData, shouldShowChannel2, editingRow, newRows, editedData, findDCavgForMTTF]);

  return (
    <>
      <CustomModal title="DCavg" show={show} setShow={setShow}>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '1rem',
            justifyItems: 'start',
            textAlign: 'start',
            fontSize: '18px',
            marginBottom: '16px',
          }}>
          <span>Evaluation Of Diagnostic Coverage (DCavg)</span>
          <span>Control System Category: Category {assessment?.category}</span>
         
          
          {/* All Channel DCavg Scores at Top */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '0.5rem',
            marginTop: '0.5rem'
          }}>
            {/* Channel 1 DCavg */}
            {dcAvgChannel1?.value > 0 && (
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '4px',
                padding: '0.75rem',
                backgroundColor: '#e3f2fd',
                borderRadius: '5px',
                border: '1px solid #2196f3'
              }}>
                {assessment?.hsr_method && hsrEquivalentDCavg ? (
                  <>
                    <span style={{fontWeight: 'bold', fontSize: '16px', color: '#1976d2'}}>
                      HSR Score: {hsrScore}
                    </span>
                    <span style={{fontWeight: 'bold', fontSize: '16px', color: '#1976d2'}}>
                      Channel 1 DCavg: {dcAvgChannel1?.value?.toFixed(2)}%
                    </span>
                    <span style={{fontWeight: 'bold', fontSize: '14px', color: '#1976d2', fontStyle: 'italic'}}>
                      (Converted from HSR)
                    </span>
                    <span style={{fontWeight: 'bold', fontSize: '16px', color: '#1976d2'}}>
                      Channel 1 DCavg Category: {dcAvgChannel1?.category || 'N/A'}
                    </span>
                  </>
                ) : (
                  <>
                    <span style={{fontWeight: 'bold', fontSize: '16px', color: '#1976d2'}}>
                      Channel 1 DCavg: {dcAvgChannel1?.value?.toFixed(2)}%
                    </span>
                    <span style={{fontWeight: 'bold', fontSize: '16px', color: '#1976d2'}}>
                      Channel 1 DCavg Category: {dcAvgChannel1?.category || 'N/A'}
                    </span>
                  </>
                )}
              </div>
            )}

            {/* Half Channel DCavg */}
            {shouldShowTeOte && dcAvgHalfChannel?.value > 0 && (
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '4px',
                padding: '0.75rem',
                backgroundColor: '#fff3e0',
                borderRadius: '5px',
                border: '1px solid #ff9800'
              }}>
                <span style={{fontWeight: 'bold', fontSize: '16px', color: '#e65100'}}>
                  Half Channel DCavg: {dcAvgHalfChannel?.value?.toFixed(2)}%
                </span>
                <span style={{fontWeight: 'bold', fontSize: '16px', color: '#e65100'}}>
                  Half Channel DCavg Category: {dcAvgHalfChannel?.category || 'N/A'}
                </span>
              </div>
            )}

            {/* Channel 2 DCavg */}
            {shouldShowChannel2 && dcAvgChannel2?.value > 0 && (
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '4px',
                padding: '0.75rem',
                backgroundColor: '#f3e5f5',
                borderRadius: '5px',
                border: '1px solid #9c27b0'
              }}>
                <span style={{fontWeight: 'bold', fontSize: '16px', color: '#6a1b9a'}}>
                  Channel 2 DCavg: {dcAvgChannel2?.value?.toFixed(2)}%
                </span>
                <span style={{fontWeight: 'bold', fontSize: '16px', color: '#6a1b9a'}}>
                  Channel 2 DCavg Category: {dcAvgChannel2?.category || 'N/A'}
                </span>
              </div>
            )}

            {/* Final DCavg (when multiple channels exist) */}
            {((shouldShowTeOte && dcAvgHalfChannel?.value > 0) || (shouldShowChannel2 && dcAvgChannel2?.value > 0)) && finalDCavg?.value > 0 && (
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '4px',
                padding: '0.75rem',
                backgroundColor: '#e8f5e9',
                borderRadius: '5px',
                border: '2px solid #4caf50'
              }}>
                <span style={{fontWeight: 'bold', fontSize: '18px', color: '#2e7d32'}}>
                  Final DCavg: {finalDCavg?.value?.toFixed(2)}%
                </span>
                <span style={{fontWeight: 'bold', fontSize: '18px', color: '#2e7d32'}}>
                  Final DCavg Category: {finalDCavg?.category || 'N/A'}
                </span>
              </div>
            )}
          </div>
        </div>

        <div style={{display: 'flex', flexDirection: 'column', gap: '1rem'}}>
          {/* Channel 1 Section */}
          <div style={{display: 'flex', flexDirection: 'column', gap: '0.5rem'}}>
            <h3 style={{fontSize: '18px', fontWeight: 'bold', marginBottom: '0.5rem'}}>Channel 1</h3>
            {channel1Tables?.map((table, index) => (
            <div
                key={`channel1-${index}`}
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '1rem',
                border: '0.5px solid gray',
                padding: '7px',
                borderRadius: '7px',
                width: '100%',
              }}>
              <EditableTable
                tableHeading={<DCImage type={table?.type} heading={table?.heading} />}
                headers={tableHeaders}
                data={table?.data}
              />
            </div>
          ))}
            <div className="text-center" style={{display: 'flex', flexDirection: 'column', gap: '4px', padding: '0.5rem', backgroundColor: '#f5f5f5', borderRadius: '5px'}}>
              {assessment?.hsr_method && hsrEquivalentDCavg ? (
                <>
                  <span style={{fontWeight: 'bold'}}>HSR Score: {hsrScore}</span>
                  <span style={{fontWeight: 'bold'}}>DCavg Equivalent: {dcAvgChannel1?.value?.toFixed(2)}%</span>
                  <span style={{fontWeight: 'bold', fontStyle: 'italic'}}>(Converted from HSR)</span>
                  <span style={{fontWeight: 'bold'}}>DCavg Category: {dcAvgChannel1?.category || 'N/A'}</span>
                </>
              ) : (
                <>
                  <span style={{fontWeight: 'bold'}}>Channel 1 DCavg: {dcAvgChannel1?.value?.toFixed(2)}%</span>
                  <span style={{fontWeight: 'bold'}}>Channel 1 DCavg Category: {dcAvgChannel1?.category || 'N/A'}</span>
                </>
              )}
            </div>
          </div>

          {/* Half Channel Section (TE, OTE) */}
          {shouldShowTeOte && (
            <div style={{display: 'flex', flexDirection: 'column', gap: '0.5rem', marginTop: '1rem', paddingTop: '1rem', borderTop: '2px solid #ccc'}}>
              <h3 style={{fontSize: '18px', fontWeight: 'bold', marginBottom: '0.5rem'}}>Half Channel</h3>
              {halfChannelTables?.map((table, index) => (
                <div
                  key={`halfchannel-${index}`}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '1rem',
                    border: '0.5px solid gray',
                    padding: '7px',
                    borderRadius: '7px',
                    width: '100%',
                  }}>
                  <EditableTable
                    tableHeading={table?.heading}
                    headers={tableHeaders}
                    data={table?.data}
                  />
                </div>
              ))}
              <div className="text-center" style={{display: 'flex', flexDirection: 'column', gap: '4px', padding: '0.5rem', backgroundColor: '#f5f5f5', borderRadius: '5px'}}>
                <span style={{fontWeight: 'bold'}}>Half Channel DCavg: {dcAvgHalfChannel?.value?.toFixed(2)}%</span>
                <span style={{fontWeight: 'bold'}}>Half Channel DCavg Category: {dcAvgHalfChannel?.category || 'N/A'}</span>
              </div>
            </div>
          )}

          {/* Channel 2 Section */}
          {shouldShowChannel2 && (
            <div style={{display: 'flex', flexDirection: 'column', gap: '0.5rem', marginTop: '1rem', paddingTop: '1rem', borderTop: '2px solid #ccc'}}>
              <h3 style={{fontSize: '18px', fontWeight: 'bold', marginBottom: '0.5rem'}}>Channel 2</h3>
              {channel2Tables?.map((table, index) => (
                <div
                  key={`channel2-${index}`}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '1rem',
                    border: '0.5px solid gray',
                    padding: '7px',
                    borderRadius: '7px',
                    width: '100%',
                  }}>
                  <EditableTable
                    tableHeading={table?.heading}
                    headers={tableHeaders}
                    data={table?.data}
                  />
                </div>
              ))}
              <div className="text-center" style={{display: 'flex', flexDirection: 'column', gap: '4px', padding: '0.5rem', backgroundColor: '#f5f5f5', borderRadius: '5px'}}>
                <span style={{fontWeight: 'bold'}}>Channel 2 DCavg: {dcAvgChannel2?.value?.toFixed(2)}%</span>
                <span style={{fontWeight: 'bold'}}>Channel 2 DCavg Category: {dcAvgChannel2?.category || 'N/A'}</span>
              </div>
            </div>
          )}

          {hasUnsavedRows && (
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              gap: '1rem',
              marginTop: '1rem',
              padding: '1rem',
              borderTop: '1px solid #ddd'
            }}>
              <button
                onClick={handleSaveAllNewRows}
                disabled={isCreatingFromHook}
                style={{
                  padding: '10px 30px',
                  backgroundColor: '#4CAF50',
                  color: 'white',
                  border: 'none',
                  borderRadius: '5px',
                  cursor: isCreatingFromHook ? 'not-allowed' : 'pointer',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  opacity: isCreatingFromHook ? 0.6 : 1,
                }}
              >
                {isCreatingFromHook ? 'Saving...' : 'Save All'}
              </button>
            </div>
          )}
        </div>

      </CustomModal>
    </>
  );
};

const DCImage=({type,heading})=>{
  const images={
    [CHANNEL_TYPES.INPUT]: dcInputImage,
    [CHANNEL_TYPES.OUTPUT]: dcOutputImage,
    [CHANNEL_TYPES.LOGIC]: dcLogicImage,

  }

  const title=(
    <Box textAlign="center">
    <img
      src={images[type]}
      alt={type}
      style={{ width: "100%", height: "100%",padding:0}}
    />
    {
      type===CHANNEL_TYPES.OUTPUT&&
      <img
      src={dcOutput2Image}
      alt="dcOutput2Image"
      style={{ width: "100%", height: "100%",padding:0}}
    />
    }
    
  </Box>
  )

  return (
    <div style={{display: 'flex', gap: '0.5rem'}}>
      <span>{heading}</span>  
      <ViewInfo
      label={title}
      tooltipStyles={{
        minWidth: 600,
        maxWidth: 800,
        maxHeight: 'none',
        overflow: 'visible',
      }}
      />
    </div>
  )




}


export default DCavg;
